/**
 * AdminApiClient
 * 
 * Handles admin-specific operations like user management, system settings, and admin analytics
 */

import { BaseApiClient } from './BaseApiClient';
import { adminApi, AdminUser, AdminTransaction, AdminClient } from '../adminApi';
import type { PlatformAIChatUsageResponse, AdminMerchantAIChatUsageResponse, AIChatMerchantsListResponse, AIChatUsageFilters } from '../../types/api/ai-chat';

export class AdminApiClient extends BaseApiClient {
  /**
   * Set token for both this client and the underlying adminApi
   */
  setToken(token: string): void {
    super.setToken(token);
    adminApi.setToken(token);
  }

  /**
   * Remove token from both this client and the underlying adminApi
   */
  removeToken(): void {
    super.removeToken();
    adminApi.removeToken();
  }

  // ============================================
  // Dashboard and Analytics
  // ============================================

  /**
   * Get admin dashboard statistics
   */
  async getDashboardStats(options?: any): Promise<any> {
    return adminApi.getDashboardStats(options);
  }

  /**
   * Get signup analytics statistics
   */
  async getSignupStats(dateRange?: string): Promise<any> {
    return this.get(`/admin/analytics/signups/stats?range=${dateRange || '30d'}`);
  }

  /**
   * Get signup attribution analytics
   */
  async getSignupAttribution(dateRange?: string): Promise<any> {
    return this.get(`/admin/analytics/signups/attribution?range=${dateRange || '30d'}`);
  }

  /**
   * Get signup trends analytics
   */
  async getSignupTrends(dateRange?: string): Promise<any> {
    return this.get(`/admin/analytics/signups/trends?range=${dateRange || '30d'}`);
  }

  /**
   * Get signup device analytics
   */
  async getSignupDevices(dateRange?: string): Promise<any> {
    return this.get(`/admin/analytics/signups/devices?range=${dateRange || '30d'}`);
  }

  /**
   * Get signup location analytics
   */
  async getSignupLocations(dateRange?: string): Promise<any> {
    return this.get(`/admin/analytics/signups/locations?range=${dateRange || '30d'}`);
  }

  // ============================================
  // Merchant Onboarding (Admin)
  // ============================================

  /**
   * Get merchant onboarding sessions with optional filters
   */
  async getMerchantOnboardingSessions(params?: {
    dateRange?: string;
    includeBusinessInfo?: boolean;
  }): Promise<any> {
    const searchParams = new URLSearchParams();
    if (params?.dateRange) searchParams.append('dateRange', params.dateRange);
    if (params?.includeBusinessInfo) searchParams.append('includeBusinessInfo', 'true');

    const query = searchParams.toString();
    return this.get(`/admin/merchant-onboarding/sessions${query ? `?${query}` : ''}`);
  }

  // ============================================
  // User Management
  // ============================================

  /**
   * Get all users with filtering
   */
  async getUsers(filters?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    status?: string;
    verified?: boolean;
    includeTestUsers?: boolean;
  }): Promise<{
    users: AdminUser[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    return adminApi.getUsers(filters);
  }

  /**
   * Get single user by ID
   */
  async getUser(userId: string): Promise<AdminUser> {
    return adminApi.getUser(userId);
  }

  /**
   * Create new user
   */
  async createUser(userData: {
    username?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    roles?: string[];
    active?: boolean;
    password?: string;
  }): Promise<AdminUser> {
    return adminApi.createUser(userData);
  }

  /**
   * Update user
   */
  async updateUser(userId: string, updates: Partial<AdminUser>): Promise<AdminUser> {
    return adminApi.updateUser(userId, updates);
  }

  /**
   * Delete user
   */
  async deleteUser(userId: string): Promise<void> {
    return adminApi.deleteUser(userId);
  }

  /**
   * Update user roles
   */
  async updateUserRoles(userId: string, roles: string[]): Promise<AdminUser> {
    return adminApi.updateUserRoles(userId, roles);
  }

  /**
   * Get user balance
   */
  async getUserBalance(userId: string): Promise<Record<string, number>> {
    return adminApi.getUserBalance(userId);
  }

  /**
   * Update user balance
   */
  async updateUserBalance(userId: string, currency: string, amount: number, operation: 'add' | 'subtract' | 'set'): Promise<void> {
    return adminApi.updateUserBalance(userId, currency, amount, operation);
  }

  // ============================================
  // Transaction Management
  // ============================================

  /**
   * Get all transactions with filtering
   */
  async getTransactions(filters?: {
    page?: number;
    limit?: number;
    search?: string;
    type?: string;
    status?: string;
    currency?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    transactions: AdminTransaction[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    return adminApi.getTransactions(filters);
  }

  /**
   * Get single transaction by ID
   */
  async getTransaction(transactionId: string): Promise<AdminTransaction> {
    return adminApi.getTransaction(transactionId);
  }

  /**
   * Update transaction
   */
  async updateTransaction(transactionId: string, updates: Partial<AdminTransaction>): Promise<AdminTransaction> {
    return adminApi.updateTransaction(transactionId, updates);
  }

  /**
   * Delete transaction
   */
  async deleteTransaction(transactionId: string): Promise<void> {
    return adminApi.deleteTransaction(transactionId);
  }

  // ============================================
  // Guest Conversations
  // ============================================

  /**
   * Get guest conversations
   */
  async getGuestConversations(filters?: {
    page?: number;
    limit?: number;
    status?: string;
    dateRange?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.dateRange) params.append('dateRange', filters.dateRange);
    
    return this.get(`/admin/guest-conversations?${params.toString()}`);
  }

  /**
   * Log guest conversation interaction
   */
  async logGuestConversation(data: {
    type: string;
    message: string;
    metadata?: any;
  }): Promise<any> {
    return this.post('/admin/guest-conversations', data);
  }

  // ============================================
  // Referral Management
  // ============================================

  /**
   * Get referral configuration
   */
  async getReferralConfig(): Promise<any> {
    return this.get('/admin/referral-config');
  }

  /**
   * Update referral configuration
   */
  async updateReferralConfig(config: any): Promise<any> {
    return this.post('/admin/referral-config', config);
  }

  /**
   * Get referral statistics
   */
  async getReferralStats(): Promise<any> {
    return this.get('/admin/referral-stats');
  }

  /**
   * Get recent referrals
   */
  async getRecentReferrals(limit: number = 10): Promise<any> {
    return this.get(`/admin/recent-referrals?limit=${limit}`);
  }

  /**
   * Get referral codes (using existing adminApi)
   */
  async getReferralCodes(filters?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    createdBy?: string;
  }): Promise<any> {
    return adminApi.getReferralCodes(filters);
  }

  /**
   * Create referral code (using existing adminApi)
   */
  async createReferralCode(referralData: {
    code: string;
    amount: number;
    currency: string;
    maxUses: number;
    expiresAt?: string;
  }): Promise<any> {
    return adminApi.createReferralCode(referralData);
  }

  // ============================================
  // Client Management
  // ============================================

  /**
   * Get admin clients
   */
  async getClients(filters?: {
    status?: string;
    environment?: string;
  }): Promise<AdminClient[]> {
    return adminApi.getClients(filters);
  }

  /**
   * Create new client
   */
  async createClient(clientData: Partial<AdminClient>): Promise<any> {
    return adminApi.createClient(clientData);
  }

  /**
   * Update client
   */
  async updateClient(clientId: string, updates: Partial<AdminClient>): Promise<AdminClient> {
    return adminApi.updateClient(clientId, updates);
  }

  /**
   * Delete client
   */
  async deleteClient(clientId: string): Promise<void> {
    return adminApi.deleteClient(clientId);
  }

  // ============================================
  // System Management
  // ============================================

  /**
   * Get system settings
   */
  async getSystemSettings(): Promise<any> {
    return adminApi.getSystemSettings();
  }

  /**
   * Update system settings
   */
  async updateSystemSettings(settings: any): Promise<void> {
    return adminApi.updateSystemSettings(settings);
  }

  /**
   * Get system health
   */
  async getSystemHealth(): Promise<any> {
    return adminApi.getSystemHealth();
  }

  /**
   * Get audit logs
   */
  async getAuditLogs(filters?: {
    page?: number;
    limit?: number;
    action?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<any> {
    return adminApi.getAuditLogs(filters);
  }

  // ============================================
  // Export Functions
  // ============================================

  /**
   * Export users
   */
  async exportUsers(format: 'csv' | 'json', filters?: any): Promise<{ downloadUrl: string }> {
    return adminApi.exportUsers(format, filters);
  }

  /**
   * Export transactions
   */
  async exportTransactions(format: 'csv' | 'json', filters?: any): Promise<{ downloadUrl: string }> {
    return adminApi.exportTransactions(format, filters);
  }

  // ============================================
  // AI Chat Usage Tracking
  // ============================================

  /**
   * Get platform-wide AI Chat usage statistics or merchant-specific usage
   * GET /api/v1/admin/ai-chat/usage
   * Returns PlatformAIChatUsageResponse if no merchantId, AdminMerchantAIChatUsageResponse if merchantId provided
   */
  async getPlatformAIChatUsageStats(filters?: AIChatUsageFilters): Promise<PlatformAIChatUsageResponse | AdminMerchantAIChatUsageResponse> {
    const params = new URLSearchParams();
    if (filters?.merchantId) params.append('merchantId', filters.merchantId);
    if (filters?.period) params.append('period', filters.period);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);

    const query = params.toString();
    return this.get<PlatformAIChatUsageResponse | AdminMerchantAIChatUsageResponse>(`/admin/ai-chat/usage${query ? `?${query}` : ''}`);
  }

  /**
   * Get list of merchants with AI Chat usage
   * GET /api/v1/admin/ai-chat/merchants
   */
  async getAIChatMerchants(filters?: {
    page?: number;
    limit?: number;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    minTokens?: number;
    maxTokens?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<AIChatMerchantsListResponse> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.minTokens) params.append('minTokens', filters.minTokens.toString());
    if (filters?.maxTokens) params.append('maxTokens', filters.maxTokens.toString());
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

    const query = params.toString();
    return this.get<AIChatMerchantsListResponse>(`/admin/ai-chat/merchants${query ? `?${query}` : ''}`);
  }

  /**
   * Get detailed AI Chat usage for a specific merchant
   * GET /api/v1/admin/ai-chat/merchants/:merchantId
   */
  async getAIChatMerchantDetails(merchantId: string, filters?: AIChatUsageFilters): Promise<AdminMerchantAIChatUsageResponse> {
    const params = new URLSearchParams();
    if (filters?.period) params.append('period', filters.period);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.includeTrends !== undefined) params.append('includeTrends', filters.includeTrends.toString());

    const query = params.toString();
    return this.get<AdminMerchantAIChatUsageResponse>(`/admin/ai-chat/merchants/${merchantId}${query ? `?${query}` : ''}`);
  }
} 